import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { mapObject } from '@client/toolkit';
import {
  ReportingContractLengthRoute,
  ReportingExpireContactRoute,
  ReportingRecievedInvoicesRoute,
  ReportingOccupancyRateRoute,
  ReportingRentalIncomeRoute,
  ReportingConsumptionEnergyRoute,
  ReportingConsumptionTemperatureRoute,
  ReportingConsumptionSensorRoute,
  OverdueInvoicesRoute,
  VacancyCostRoute,
  NetYieldRoute,
} from './';
import { ReportingVatPercentageRoute } from './reporting-vat-percentage';
import { ReportPageLayout } from '../../components/layout';
import { PackageTierProtectedRoute } from '../../components/package-tier-protected-route';
import { packageRulesConfig } from '../../hooks/useUserPackages';
import { ReportingEnergyCostRoute } from './reporting-energy-cost/reporting-energy-cost.route';
import { ReportingVentilationDemandRoute } from './reporting-ventilation-demand/reporting-ventilation-demand.route';
import { ReportingConsumptionBySourceRoute } from './reporting-consumption-by-source/reporting-consumption-by-source.route';
import { ReportingPortfolioOverviewRoute } from './reporting-portfolio-overview/reporting-portfolio-overview.route';
import { ReportingPortfolioOverview2Route } from './reporting-portfolio-overview-2/reporting-portfolio-overview-2.route';
import { ReportingPortfolioDiversificationRoute } from './reporting-portfolio-diversification/reporting-portfolio-diversification.route';
import { ReportingEnergyProfileRoute } from './reporting-energy-profile/reporting-energy-profile.route';
import { ReportingFinancialOverviewRoute } from './reporting-financial-overview/reporting-financial-overview.route';
import { ReportingFinancingRoute } from './reporting-financing/reporting-financing.route';

export const ReportingPaths = {
  Home: '/reporting',
  RentalIncome: '/reporting/rental-income',
  OccupancyRate: '/reporting/occupancy-rate',
  ContractLength: '/reporting/contract-length',
  ExpireContract: '/reporting/expire-contract',
  VatPercentage: '/reporting/vat-percentage',
  Invoices: '/reporting/invoices',
  OverdueInvoices: '/reporting/overdue-invoices',
  VacancyCost: '/reporting/vacancy-cost',
  NetYieldByBuilding: '/reporting/net-yield',
  Area: '/reporting/area',
  ConsumptionEnergy: '/reporting/consumption-energy',
  ConsumptionOccupancy: '/reporting/consumption-occupancy',
  ConsumptionData: '/reporting/consumption-data',
  EconomyEnergyConsumption: '/reporting/economy-energy-consumption',
  ConsumptionTemperature: '/reporting/consumption-temperature',
  ConsumptionBySensor: '/reporting/consumption-by-sensor',
  VentilationVsDemand: '/reporting/ventilation-vs-demand',
  ConsumptionBySource: '/reporting/consumption-by-source',
  PortfolioOverview: '/reporting/portfolio-overview',
  PortfolioOverview2: '/reporting/portfolio-overview-2',
  PortfolioDiversification: '/reporting/portfolio-diversification',
  EnergyProfile: '/reporting/energy-profile',
  FinancialOverview: '/reporting/financial-overview',
  Financing: '/reporting/financing',
};

const RouterPaths = mapObject(ReportingPaths, (val: string) => val.replace(ReportingPaths.Home, ''));

export function ReportingRouter() {
  const { search } = useLocation();
  return (
    <Routes>
      <Route path={RouterPaths.Home} element={<ReportPageLayout />}>
        <Route
          path={RouterPaths.RentalIncome}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.management}>
              <ReportingRentalIncomeRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.OccupancyRate}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.management}>
              <ReportingOccupancyRateRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.ContractLength}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.management}>
              <ReportingContractLengthRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.ExpireContract}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.management}>
              <ReportingExpireContactRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.VatPercentage}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.management}>
              <ReportingVatPercentageRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.Invoices}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.economy}>
              <ReportingRecievedInvoicesRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.OverdueInvoices}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.economy}>
              <OverdueInvoicesRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.VacancyCost}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.economy}>
              <VacancyCostRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.NetYieldByBuilding}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.economy}>
              <NetYieldRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.ConsumptionEnergy}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
              <ReportingConsumptionEnergyRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.EconomyEnergyConsumption}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
              <ReportingEnergyCostRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.ConsumptionTemperature}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
              <ReportingConsumptionTemperatureRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.ConsumptionBySensor}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
              <ReportingConsumptionSensorRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.VentilationVsDemand}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
              <ReportingVentilationDemandRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.ConsumptionBySource}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.eos}>
              <ReportingConsumptionBySourceRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.PortfolioOverview}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
              <ReportingPortfolioOverviewRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.PortfolioOverview2}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
              <ReportingPortfolioOverview2Route />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.PortfolioDiversification}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
              <ReportingPortfolioDiversificationRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.EnergyProfile}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
              <ReportingEnergyProfileRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.FinancialOverview}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
              <ReportingFinancialOverviewRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route
          path={RouterPaths.Financing}
          element={
            <PackageTierProtectedRoute requiredPermissions={packageRulesConfig.portfolio}>
              <ReportingFinancingRoute />
            </PackageTierProtectedRoute>
          }
        />
        <Route index element={<Navigate to={ReportingPaths.RentalIncome + search} replace />} />
      </Route>

      <Route path="*" element={<Navigate to={ReportingPaths.RentalIncome + search} replace />} />
    </Routes>
  );
}
